import { LogGaEvent } from "lib/GoogleAnalytics"
import React, { useState } from "react"
import styled from "styled-components"
import ArrowDown from "url:assets/images/icons/ArrowDown.svg"
import { EventAction, EventCategory } from "models/GoogleAnalytics"

const AccordionButton = styled.button<{ isOpen: boolean }>`
  background: none;
  border: none;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: 0.2s;
  padding: 15px 0;
  cursor: pointer;

  .arrow-icon {
    margin-left: 10px;
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
    ::after {
      content: url(${ArrowDown});
    }
  }
`

const ButtonName = styled.div`
  display: flex;
  align-items: center;

  p {
    font-size: 18px;
    font-weight: 550;
    margin: 0;
    text-align: start;
  }
`

const Status = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-right: 10px;
  &.Poor {
    color: red;
  }
  &.Fair {
    color: orange;
  }
  &.Good {
    color: blue;
  }
  &.Great {
    color: blue;
  }
  &.Unknown {
    color: gray;
  }
`

export const AccordionPanel = styled.div<{ isOpen: boolean }>`
  max-height: ${({ isOpen }) => (isOpen ? "fit-content" : "0")};
  overflow: hidden;
  transition: 0.8s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[300]};
  :last-child {
    border-bottom: none;
  }
`

const enum Ratings {
  Great = "Great",
  AlmostThere = "Almost there",
  NeedsWork = "Needs work",
  default = "default",
}

export type AccordionDataSection = {
  /**
   * The accordion's clickable title
   */
  name: string
  status?: "Poor" | "Fair" | "Good" | "Great" | "Unknown"
  id: string
  /**
   * The accordion's content
   */
  page: JSX.Element
  titleState?: Ratings
  statusComponent?: JSX.Element
  trackingLabelPrefix?: string
}

type Props = {
  data: AccordionDataSection[]
}

export const Accordion = ({ data }: Props) => {
  const [openState, setOpenState] = useState<boolean[]>([])
  return (
    <>
      {data.map(
        (
          { name, status, id, page, statusComponent, trackingLabelPrefix, titleState = "" },
          idx
        ) => {
          return (
            <React.Fragment key={id + "-tab"}>
              <AccordionButton
                className={id + "-button"}
                onClick={() => {
                  LogGaEvent({
                    action: openState[idx] ? EventAction.closeAccordion : EventAction.openAccordion,
                    event_category: EventCategory.functionalInteraction,
                    event_label: `${trackingLabelPrefix ? trackingLabelPrefix + " - " : ""}${name}`,
                  })
                  setOpenState(data.map((_, i) => idx === i && !openState[i]))
                }}
                isOpen={openState[idx]}
              >
                <ButtonName>
                  {status && <div className={"icon " + status} />}
                  <p className="name">{name}</p>
                </ButtonName>
                <Status>
                  {statusComponent}
                  <div className="arrow-icon" />
                </Status>
              </AccordionButton>
              <AccordionPanel isOpen={openState[idx]}>{page}</AccordionPanel>
            </React.Fragment>
          )
        }
      )}
    </>
  )
}
